import { getUserPermissions, getUserRole } from "redux/selectors";
import { useAppSelector } from "./redux";
import { Permission, initialPermissions } from "consts";

export const checkIsAgent = (roleName: string) =>
  roleName.toLowerCase() === "agent";

export const checkIsManager = (roleName: string) =>
  roleName.toLowerCase() === "manager";

export const usePermissions = (): Record<
  keyof typeof initialPermissions,
  boolean
> => {
  const userPermissions = useAppSelector(getUserPermissions);
  const userRole = useAppSelector(getUserRole);

  if (!userPermissions?.length) {
    return initialPermissions;
  }

  const userCan: typeof initialPermissions = userPermissions
    .map(({ name }) => name)
    .reduce(
      (
        acc: Record<keyof typeof initialPermissions, boolean>,
        el: string,
      ): Record<keyof typeof initialPermissions, boolean> => {
        switch (el) {
          /* Dashboard */
          case Permission.StatisticsGet:
            acc.canStatisticsGet = true;
            break;

          /* Users section */
          case Permission.UsersGet:
            acc.canUsersGet = true;
            break;
          case Permission.UsersCreate:
            acc.canUsersCreate = true;
            break;
          case Permission.UserAgentOnlyCreate:
            acc.canUserAgentOnlyCreate = true;
            break;
          case Permission.UsersUpdate:
            acc.canUsersUpdate = true;
            break;
          case Permission.UsersDelete:
            acc.canUsersDelete = true;
            break;

          /* Roles section */
          case Permission.PermissionsGet:
            acc.canPermissionsGet = true;
            break;
          case Permission.RolesGet:
            acc.canRolesGet = true;
            break;
          case Permission.RolesCreate:
            acc.canRolesCreate = true;
            break;
          case Permission.RolesUpdate:
            acc.canRolesUpdate = true;
            break;
          case Permission.RolesDelete:
            acc.canRolesDelete = true;
            break;

          /* Transactions section */
          case Permission.TransactionsCreate:
            acc.canTransactionsCreate = true;
            break;
          case Permission.TransactionsGet:
            acc.canTransactionsGet = true;
            break;

          /* Customers section */
          case Permission.CustomerBase:
            acc.canCustomerBase = true;
            break;
          case Permission.CustomersGet:
            acc.canCustomersGet = true;
            break;
          case Permission.CustomersActivate:
            acc.canCustomersActivate = true;
            break;
          case Permission.BalanceGet:
            acc.canBalanceGet = true;
            break;

          /* Delivery areas section */
          case Permission.LocationsUpdate:
            acc.canLocationsUpdate = true;
            break;
          case Permission.LocationsGet:
            acc.canLocationsGet = true;
            break;
          case Permission.LocationsMenuGet:
            acc.canLocationsMenuGet = true;
            break;

          /* Settings section */
          case Permission.ExchangeRateUpdate:
            acc.canExchangeRateUpdate = true;
            break;
          case Permission.BalanceExpirationUpdate:
            acc.canBalanceExpirationUpdate = true;
            break;
          case Permission.SettingsGet:
            acc.canSettingsGet = true;
            break;
          case Permission.SettingsUpdate:
            acc.canSettingsUpdate = true;
            break;
          case Permission.BalanceExpirationAgentLimitUpdate:
            acc.isBalanceExpirationAgentLimitUpdate = true;
            break;
          case Permission.BalanceExpirationManagerLimitUpdate:
            acc.isBalanceExpirationManagerLimitUpdate = true;
            break;
          case Permission.RefundAddAgentLimitUpdate:
            acc.isRefundAddAgentLimitUpdate = true;
            break;
          case Permission.RefundAddManagerLimitUpdate:
            acc.isAddManagerLimitUpdate = true;
            break;

          /* other */
          case Permission.EmployeesGet:
            acc.canEmployeesGet = true;
            break;
          case Permission.EmployeesCreate:
            acc.canEmployeesCreate = true;
            break;
          case Permission.InvitationsCreate:
            acc.canInvitationsCreate = true;
            break;
          case Permission.InvitationsGet:
            acc.canInvitationsGet = true;
            break;
          case Permission.OrdersGet:
            acc.canOrdersGet = true;
            break;
          case Permission.PackagesCreate:
            acc.canPackagesCreate = true;
            break;
          case Permission.PackagesUpdate:
            acc.canPackagesUpdate = true;
            break;
          case Permission.DeliveriesAllOptions:
            acc.canDeliveriesAllOptions = true;
            break;
        }

        return acc;
      },
      initialPermissions,
    );

  userCan.isUserAgent = checkIsAgent(userRole);
  userCan.isUserManager = checkIsManager(userRole);

  return userCan;
};
